'use client';
import { useEffect, useState } from 'react';
import * as All from '@/element/common/elmntbase';
import { getCookieConsent, saveCookieConsent } from '@/lib/service/cookieconsent';

export default function CookieConsent() {
    const [show, setShow] = useState(false);
    const [data, setData] = useState<any>(null);
    const [detailsShown, setDetailsShown] = useState(false);
    const [consents, setConsents] = useState<any>({});

    useEffect(() => {
        const checkConsent = async () => {
            if (typeof window === 'undefined') return;

            const consentCookie = document.cookie.split('; ').find(row => row.startsWith('cookie_consent='));
            if (!consentCookie) {
                const result = await getCookieConsent();
                if (result && result.status === 1) {
                    setData(result.cookieconsent);
                    setShow(true);

                    const initialConsents: any = {};
                    result.cookieconsent.allCookieOptions?.forEach((option: any) => {
                        initialConsents[option.uid] = !!option.necessary;
                    });
                    setConsents(initialConsents);
                }
            }
        };
        checkConsent();
    }, []);

    if (!show || !data) return null;

    const handleSave = async (all = false) => {
        const selectedIds = all
            ? data.allCookieOptions.map((o: any) => o.uid)
            : data.allCookieOptions.filter((o: any) => o.necessary || consents[o.uid]).map((o: any) => o.uid);

        const cookieValue = JSON.stringify(selectedIds);
        const expires = new Date();
        expires.setFullYear(expires.getFullYear() + 1);
        document.cookie = `cookie_consent=${cookieValue}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;

        // Sync with backend
        await saveCookieConsent(all ? { selectAll: true } : { cookieOptions: selectedIds }, data.sysLanguageUid || 0);

        setShow(false);
    };

    const handleDeny = async () => {
        const necessaryIds = data.allCookieOptions.filter((o: any) => o.necessary).map((o: any) => o.uid);
        const cookieValue = JSON.stringify(necessaryIds);
        const expires = new Date();
        expires.setFullYear(expires.getFullYear() + 1);
        document.cookie = `cookie_consent=${cookieValue}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;

        // Sync with backend
        await saveCookieConsent({ deny: true }, data.sysLanguageUid || 0);

        setShow(false);
    };

    const toggleConsent = (uid: number) => {
        setConsents((prev: any) => ({ ...prev, [uid]: !prev[uid] }));
    };

    const toggleCategory = (categoryId: number) => {
        const optionsInCategory = data.allCookieOptions.filter((o: any) => o.cookieCategory?.uid === categoryId && !o.necessary);
        const allSelected = optionsInCategory.every((o: any) => consents[o.uid]);
        const newConsents = { ...consents };
        optionsInCategory.forEach((o: any) => {
            newConsents[o.uid] = !allSelected;
        });
        setConsents(newConsents);
    };

    const isCategorySelected = (categoryId: number) => {
        const optionsInCategory = data.allCookieOptions.filter((o: any) => o.cookieCategory?.uid === categoryId && !o.necessary);
        if (optionsInCategory.length === 0) return true;
        return optionsInCategory.every((o: any) => consents[o.uid]);
    };


    return (
        <div className={`tc-cookie-bar ${show ? 'show' : ''}`}>
            <div className="tc-cookie-bar-container">
                <div className="tc-cookie-bar-content">
                    <div className="tc-cookie-bar-main">
                        <div className="tc-cookie-bar-text">
                            <h5 className="fw-bold mb-1">{data.header}</h5>
                            <div className="cookie-hint fsz-14 cr-777 mb-0" dangerouslySetInnerHTML={{ __html: data.hint }} />
                        </div>
                        <div className="tc-cookie-bar-actions d-flex align-items-center gap-3">
                            <All.Button
                                variant="link"
                                onClick={() => setDetailsShown(!detailsShown)}
                                className="p-0 text-dark fw-bold fsz-14 text-decoration-none d-flex align-items-center shadow-none me-2"
                            >
                                {detailsShown ? (data.hideDetailsLabel || 'Hide details') : (data.showDetailsLabel || 'Show details')}
                                <i className={`ms-1 fa fa-chevron-${detailsShown ? 'up' : 'down'}`}></i>
                            </All.Button>

                            <All.Button variant="outline-dark" className="rounded-pill px-3 fsz-13 fw-bold shadow-none" onClick={() => handleSave(true)}>
                                {data.selectAllLabel || 'Select all'}
                            </All.Button>
                            <All.Button variant="light" className="rounded-pill px-3 fsz-13 fw-bold text-secondary shadow-none" onClick={handleDeny}>
                                {data.denyLabel || 'Deny'}
                            </All.Button>
                            <All.Button variant="dark" className="rounded-pill px-4 fsz-13 fw-bold shadow-sm" onClick={() => handleSave(false)}>
                                {data.saveLabel || 'Save'}
                            </All.Button>
                        </div>
                    </div>

                    {detailsShown && (
                        <All.MotionDiv
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            className="cookie-details-container overflow-hidden mt-3 pt-3 border-top"
                        >
                            <div className="row g-3">
                                {/* Necessary Cookies Section */}
                                <div className="col-md-4">
                                    <div className="cookie-category-box p-3 bg-light rounded-4 border h-100">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <h6 className="mb-0 fw-bold fsz-16">Necessary</h6>
                                            <div className="form-check form-switch">
                                                <input className="form-check-input" type="checkbox" checked disabled />
                                            </div>
                                        </div>
                                        <div dangerouslySetInnerHTML={{ __html: data.necessaryCookiesInfo }} className="fsz-12 cr-666 mb-2" />
                                    </div>
                                </div>

                                {/* Other Categories */}
                                {data.cookieCategories?.map((category: any) => {
                                    const options = data.allCookieOptions.filter((o: any) => o.cookieCategory?.uid === category.uid);
                                    if (options.length === 0) return null;

                                    return (
                                        <div key={category.uid} className="col-md-4">
                                            <div className="cookie-category-box p-3 rounded-4 border h-100">
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <h6 className="mb-0 fw-bold fsz-16">{category.label}</h6>
                                                    <div className="form-check form-switch">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            checked={isCategorySelected(category.uid)}
                                                            onChange={() => toggleCategory(category.uid)}
                                                        />
                                                    </div>
                                                </div>
                                                <div dangerouslySetInnerHTML={{ __html: category.info }} className="fsz-12 cr-666 mb-2" />
                                                <div className="options-list mt-2 pt-2 border-top">
                                                    {options.map((option: any) => (
                                                        <div key={option.uid} className="option-item mb-2">
                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <span className="fsz-12 fw-bold">{option.label}</span>
                                                                <div className="form-check form-switch">
                                                                    <input 
                                                                        className="form-check-input" 
                                                                        type="checkbox" 
                                                                        style={{ width: '24px', height: '12px' }}
                                                                        checked={!!consents[option.uid]} 
                                                                        onChange={() => toggleConsent(option.uid)}
                                                                    />
                                                                </div>
                                                            </div>
                                                            {option.purpose && <div className="fsz-11 cr-777 mt-1">{option.purpose}</div>}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="modal-footer-links mt-3 d-flex fsz-11">
                                {data.imprint && (
                                    <All.MotionA href={data.imprint} target="_blank" className="text-secondary text-decoration-none me-3">
                                        Imprint
                                    </All.MotionA>
                                )}
                                {data.datapolicy && (
                                    <All.MotionA href={data.datapolicy} target="_blank" className="text-secondary text-decoration-none">
                                        Datapolicy
                                    </All.MotionA>
                                )}
                            </div>
                        </All.MotionDiv>
                    )}
                </div>
            </div>
            <style jsx>{`
                .tc-cookie-bar {
                    position: fixed;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    z-index: 9999;
                    display: none;
                    padding-bottom: 30px;
                }
                .tc-cookie-bar.show {
                    display: block;
                }
                .tc-cookie-bar-container {
                    width: 70%;
                    margin-left: 30px;
                    background: #fff;
                    border-radius: 20px;
                    box-shadow: 0 10px 40px rgba(0,0,0,0.15);
                    border: 1px solid rgba(0,0,0,0.05);
                    padding: 15px 30px;
                }
                .tc-cookie-bar-main {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 30px;
                }
                .tc-cookie-bar-text {
                    flex: 1;
                }
                .tc-cookie-bar-actions {
                    flex-shrink: 0;
                }
                .cookie-category-box {
                    transition: all 0.3s ease;
                    background: #fff;
                }
                .cookie-category-box:hover {
                    border-color: #000 !important;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
                }
                .fsz-16 { font-size: 16px; }
                .fsz-14 { font-size: 14px; }
                .fsz-13 { font-size: 13px; }
                .fsz-12 { font-size: 12px; }
                .fsz-11 { font-size: 11px; }
                .cr-777 { color: #777; }
                .cr-666 { color: #666; }
                
                :global(.form-check-input:checked) {
                    background-color: #000;
                    border-color: #000;
                }
                :global(.form-check-input:focus) {
                    box-shadow: none;
                    border-color: #000;
                }
            `}</style>
        </div>
    );
}
